import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    PlusIcon, 
    TrashIcon, 
    BuildingOfficeIcon,
    TruckIcon,
    CheckCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const ReturnSettingsPanel = () => {
    const [settings, setSettings] = useState({
        returnAddresses: [],
        shippingCompanies: [],
        defaultShippingCompany: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newAddress, setNewAddress] = useState({
        title: '',
        name: '',
        address_line: '',
        district: '',
        city: '',
        postal_code: '',
        phone: '',
        isDefault: false
    });
    const [showAddressForm, setShowAddressForm] = useState(false);
    const { token } = useAuth();

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/api/settings/return-settings', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(response);
        } catch (error) {
            toast.error('İade ayarları yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.put('/api/settings/return-settings', settings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('İade ayarları başarıyla kaydedildi');
        } catch (error) {
            toast.error('İade ayarları kaydedilirken hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const addAddress = () => {
        if (!newAddress.title || !newAddress.address_line || !newAddress.city) {
            toast.warn('Lütfen zorunlu alanları doldurun');
            return;
        }

        const addressWithId = {
            ...newAddress,
            id: Date.now(),
            isDefault: settings.returnAddresses.length === 0 || newAddress.isDefault
        };

        // Eğer yeni adres default ise, diğerlerini default olmaktan çıkar
        const updatedAddresses = newAddress.isDefault 
            ? settings.returnAddresses.map(addr => ({ ...addr, isDefault: false }))
            : settings.returnAddresses;

        setSettings(prev => ({
            ...prev,
            returnAddresses: [...updatedAddresses, addressWithId]
        }));

        setNewAddress({
            title: '',
            name: '',
            address_line: '',
            district: '',
            city: '',
            postal_code: '',
            phone: '',
            isDefault: false
        });
        setShowAddressForm(false);
        toast.success('Adres eklendi');
    };

    const removeAddress = (id) => {
        setSettings(prev => ({
            ...prev,
            returnAddresses: prev.returnAddresses.filter(addr => addr.id !== id)
        }));
        toast.success('Adres silindi');
    };

    const setDefaultAddress = (id) => {
        setSettings(prev => ({
            ...prev,
            returnAddresses: prev.returnAddresses.map(addr => ({
                ...addr,
                isDefault: addr.id === id
            }))
        }));
    };

    const addShippingCompany = () => {
        const company = prompt('Yeni kargo şirketi adı:');
        if (company && !settings.shippingCompanies.includes(company)) {
            setSettings(prev => ({
                ...prev,
                shippingCompanies: [...prev.shippingCompanies, company]
            }));
            toast.success('Kargo şirketi eklendi');
        }
    };

    const removeShippingCompany = (company) => {
        setSettings(prev => ({
            ...prev,
            shippingCompanies: prev.shippingCompanies.filter(c => c !== company),
            defaultShippingCompany: prev.defaultShippingCompany === company ? '' : prev.defaultShippingCompany
        }));
        toast.success('Kargo şirketi silindi');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 space-y-8">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">İade Ayarları</h1>
                        <p className="text-gray-600 mt-1">İade adreslerini ve kargo şirketlerini yönetin</p>
                    </div>
                    <button
                        onClick={saveSettings}
                        disabled={saving}
                        className="bg-brand-green hover:bg-brand-dark-green text-white px-6 py-3 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        <CheckCircleIcon className="h-5 w-5" />
                        {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* İade Adresleri */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <BuildingOfficeIcon className="h-6 w-6 text-brand-green" />
                            <h2 className="text-xl font-semibold text-gray-900">İade Adresleri</h2>
                        </div>
                        <button
                            onClick={() => setShowAddressForm(true)}
                            className="bg-brand-green hover:bg-brand-dark-green text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Adres Ekle
                        </button>
                    </div>

                    {/* Adres Listesi */}
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                        {settings.returnAddresses.map((address) => (
                            <div key={address.id} className="border border-gray-200 rounded-xl p-4 hover:border-brand-green transition-colors">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="font-medium text-gray-900">{address.title}</h3>
                                            {address.isDefault && (
                                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                                    Varsayılan
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600">{address.name}</p>
                                        <p className="text-sm text-gray-600">{address.address_line}</p>
                                        <p className="text-sm text-gray-600">{address.district}, {address.city} {address.postal_code}</p>
                                        {address.phone && <p className="text-sm text-gray-600">{address.phone}</p>}
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                        {!address.isDefault && (
                                            <button
                                                onClick={() => setDefaultAddress(address.id)}
                                                className="text-brand-green hover:text-brand-dark-green text-sm font-medium"
                                            >
                                                Varsayılan Yap
                                            </button>
                                        )}
                                        <button
                                            onClick={() => removeAddress(address.id)}
                                            className="text-red-600 hover:text-red-700 p-1"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Adres Ekleme Formu */}
                    {showAddressForm && (
                        <div className="mt-6 border-t pt-6">
                            <h3 className="font-medium text-gray-900 mb-4">Yeni Adres Ekle</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="Adres Başlığı *"
                                    value={newAddress.title}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, title: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                                <input
                                    type="text"
                                    placeholder="Firma/Kişi Adı"
                                    value={newAddress.name}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, name: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                                <input
                                    type="text"
                                    placeholder="Adres Satırı *"
                                    value={newAddress.address_line}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, address_line: e.target.value }))}
                                    className="sm:col-span-2 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                                <input
                                    type="text"
                                    placeholder="İlçe"
                                    value={newAddress.district}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, district: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                                <input
                                    type="text"
                                    placeholder="Şehir *"
                                    value={newAddress.city}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                                <input
                                    type="text"
                                    placeholder="Posta Kodu"
                                    value={newAddress.postal_code}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                                <input
                                    type="text"
                                    placeholder="Telefon"
                                    value={newAddress.phone}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, phone: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                />
                            </div>
                            <div className="flex items-center gap-2 mt-4">
                                <input
                                    type="checkbox"
                                    id="isDefault"
                                    checked={newAddress.isDefault}
                                    onChange={(e) => setNewAddress(prev => ({ ...prev, isDefault: e.target.checked }))}
                                    className="rounded border-gray-300 text-brand-green focus:ring-brand-green"
                                />
                                <label htmlFor="isDefault" className="text-sm text-gray-700">
                                    Varsayılan adres olarak ayarla
                                </label>
                            </div>
                            <div className="flex gap-3 mt-4">
                                <button
                                    onClick={addAddress}
                                    className="bg-brand-green hover:bg-brand-dark-green text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                    Ekle
                                </button>
                                <button
                                    onClick={() => setShowAddressForm(false)}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                    İptal
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Kargo Şirketleri */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <TruckIcon className="h-6 w-6 text-brand-green" />
                            <h2 className="text-xl font-semibold text-gray-900">Kargo Şirketleri</h2>
                        </div>
                        <button
                            onClick={addShippingCompany}
                            className="bg-brand-green hover:bg-brand-dark-green text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Şirket Ekle
                        </button>
                    </div>

                    {/* Varsayılan Kargo Şirketi */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Varsayılan Kargo Şirketi
                        </label>
                        <select
                            value={settings.defaultShippingCompany}
                            onChange={(e) => setSettings(prev => ({ ...prev, defaultShippingCompany: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-green focus:border-transparent"
                        >
                            <option value="">Seçiniz...</option>
                            {settings.shippingCompanies.map(company => (
                                <option key={company} value={company}>{company}</option>
                            ))}
                        </select>
                    </div>

                    {/* Kargo Şirketleri Listesi */}
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {settings.shippingCompanies.map((company) => (
                            <div key={company} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-brand-green transition-colors">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-900">{company}</span>
                                    {settings.defaultShippingCompany === company && (
                                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                            Varsayılan
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => removeShippingCompany(company)}
                                    className="text-red-600 hover:text-red-700 p-1"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReturnSettingsPanel;